import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NgbDropdown } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  AgencyContextService,
  AgencyContextStore,
} from "src/app/core/services/agency-context.service";

/** Valor da opção "Todas as agências" na lista. */
const ALL_AGENCIES = 0;

/** Pesquisa sem distinguir maiúsculas nem acentos ("Évora" ↔ "evora"). */
const normalize = (value: string): string =>
  (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/** Páginas de detalhe de negócio: `/deals/<tipo>/<id>`. */
const DEAL_DETAIL_URL = /^\/deals\/(sale|rent|listing|process)\/(\d+)(?:[/?#]|$)/;

/**
 * Seletor global de agência do MASTER, no topbar.
 *
 * Novo e independente do seletor antigo de país/agências (que fica escondido):
 * não lê nem escreve as preferências desse. Trocar de agência sai da página
 * atual — o que faz correr os avisos de alterações por gravar — e volta a
 * abri-la já com a nova agência; um detalhe de negócio que não pertença à
 * agência dá lugar à lista.
 */
@Component({
  selector: "app-agency-context-selector",
  templateUrl: "./agency-context-selector.component.html",
  styleUrls: ["./agency-context-selector.component.scss"],
})
export class AgencyContextSelectorComponent implements OnInit, OnDestroy {
  options: AgencyContextStore[] = [];
  selectedId = ALL_AGENCIES;
  activeName = "";
  switching = false;
  search = "";

  @ViewChild("searchInput") private searchInput?: ElementRef<HTMLInputElement>;

  private stores: AgencyContextStore[] = [];
  private allLabel = "";
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly agencyContext: AgencyContextService,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly toastr: ToastrService,
  ) {}

  get isActive(): boolean {
    return this.selectedId !== ALL_AGENCIES;
  }

  ngOnInit(): void {
    this.translate
      .stream("t-agency-context-all")
      .pipe(takeUntil(this.destroy$))
      .subscribe((label: string) => {
        this.allLabel = label;
        this.buildOptions();
      });

    this.agencyContext
      .loadStores()
      .pipe(takeUntil(this.destroy$))
      .subscribe((stores) => {
        this.stores = stores;
        this.buildOptions();
      });

    this.agencyContext.store$.pipe(takeUntil(this.destroy$)).subscribe((store) => {
      if (this.switching) return;
      this.selectedId = store?.id ?? ALL_AGENCIES;
      this.activeName = store?.name ?? "";
    });

    // O servidor recusou a agência (já repostas "todas"): a página atual pode
    // ter ficado a meio, por isso é aberta de novo.
    this.agencyContext.invalid$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.toastr.warning(this.translate.instant("t-agency-context-invalid"));
      this.reopenCurrentPage();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get filteredOptions(): AgencyContextStore[] {
    const term = normalize(this.search);
    if (!term) return this.options;
    return this.options.filter((option) => normalize(option.name).includes(term));
  }

  onOpenChange(open: boolean): void {
    if (!open) return;
    this.search = "";
    setTimeout(() => this.searchInput?.nativeElement.focus());
  }

  choose(id: number, dropdown: NgbDropdown): void {
    dropdown.close();
    this.onSelect(id);
  }

  /** Enter na pesquisa escolhe o primeiro resultado. */
  selectFirst(dropdown: NgbDropdown): void {
    const first = this.filteredOptions[0];
    if (first) this.choose(first.id, dropdown);
  }

  onSelect(id: number): void {
    const previousId = this.agencyContext.storeId ?? ALL_AGENCIES;
    this.selectedId = id ?? ALL_AGENCIES;
    if (this.selectedId === previousId || this.switching) return;

    const store =
      this.selectedId === ALL_AGENCIES
        ? null
        : this.stores.find((one) => one.id === this.selectedId) ?? null;
    this.switchTo(store, previousId);
  }

  private async switchTo(store: AgencyContextStore | null, previousId: number): Promise<void> {
    this.switching = true;
    const url = this.router.url;
    try {
      // Sair primeiro: se houver alterações por gravar e o utilizador ficar,
      // a agência não muda.
      const left = await this.router.navigateByUrl("/agency-context-switch", {
        skipLocationChange: true,
      });
      if (!left) {
        this.selectedId = previousId;
        return;
      }

      this.agencyContext.select(store);
      this.activeName = store?.name ?? "";
      await this.router.navigateByUrl(await this.resolveTarget(url, store));
    } catch {
      // Navegação falhada: o contexto é o que ficou no serviço.
      this.selectedId = this.agencyContext.storeId ?? ALL_AGENCIES;
    } finally {
      this.switching = false;
    }
  }

  /** A página a reabrir: a mesma, salvo um detalhe que não é da agência. */
  private async resolveTarget(url: string, store: AgencyContextStore | null): Promise<string> {
    const dealDetail = store ? DEAL_DETAIL_URL.exec(url) : null;
    if (!dealDetail) return url;

    try {
      const { belongs } = await this.agencyContext
        .checkDeal(dealDetail[1], Number(dealDetail[2]))
        .toPromise();
      if (belongs) return url;
    } catch {
      // Sem confirmação não se arrisca mostrar o detalhe na agência errada.
    }
    this.toastr.info(this.translate.instant("t-agency-context-left-detail"));
    return "/deals";
  }

  private async reopenCurrentPage(): Promise<void> {
    if (this.switching) return;
    const url = this.router.url;
    const left = await this.router.navigateByUrl("/agency-context-switch", {
      skipLocationChange: true,
    });
    if (left) await this.router.navigateByUrl(url);
  }

  private buildOptions(): void {
    this.options = [{ id: ALL_AGENCIES, name: this.allLabel }, ...this.stores];
  }
}
